import { 
  InAppNotification, 
  NotificationAction,
  NotificationType,
  NotificationCategory
} from '../../types/intelligentScheduling';
import { getNotificationConfig } from './notificationConfigs';

/**
 * Smart Notification Service for Intelligent Scheduling
 * 
 * This service manages context-aware notifications that help users stay on track
 * with their intelligent schedules. It learns from user behavior and optimizes
 * notification timing for maximum effectiveness while minimizing fatigue.
 */
export class SmartNotificationService {
  private activeNotifications: InAppNotification[] = [];
  private listeners: Set<(event: string, data: any) => void> = new Set();

  constructor() {
    this.startNotificationProcessor();
    this.setupServiceWorkerMessageListener();
    
    // Validate schedule data consistency in development
    if (process.env.NODE_ENV === 'development') {
      // Run validation after a short delay to ensure services are initialized
      setTimeout(() => {
        this.validateScheduleDataConsistency();
      }, 1000);
    }
  }

  // === MAIN NOTIFICATION API ===

  /**
   * Creates and sends a smart notification for a schedule
   */
  async sendScheduleNotification(
    scheduleId: string,
    listId: string,
    title: string,
    message: string,
    notificationType: NotificationType = 'schedule_reminder',
    options: {
      priority?: 'low' | 'medium' | 'high' | 'critical';
      confidence?: number;
      contextFactors?: string[];
      customActions?: NotificationAction[];
    } = {}
  ): Promise<void> {
    console.log('🚀 sendScheduleNotification called with:', { scheduleId, listId, title, message, notificationType, options });
    
    const config = getNotificationConfig(notificationType);
    
    const notification: InAppNotification = {
      id: this.generateNotificationId(),
      scheduleId,
      type: notificationType,
      title,
      message,
      priority: options.priority || 'medium',
      triggerTime: new Date(),
      createdAt: new Date(),
      expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000), // 24 hours
      isRead: false,
      isPersistent: options.priority === 'high' || options.priority === 'critical',
      contextualFactors: options.contextFactors || [],
      confidence: options.confidence || 0.7,
      actions: options.customActions || config.defaultActions,
      iconType: this.getIconForType(notificationType),
      color: this.getColorForType(notificationType),
      dismissible: config.category !== NotificationCategory.WARNING && options.priority !== 'critical'
    };

    // Check if we should send this notification
    const shouldSend = await this.shouldSendNotification(notification);
    if (!shouldSend.shouldSend) {
      console.log('🚫 Notification not sent:', shouldSend.reason);
      return;
    }

    // Add to active notifications
    this.activeNotifications.push(notification);
    
    // Save to storage
    await this.saveNotificationToStorage(notification);
    
    // Send browser push notification
    await this.sendBrowserNotification(notification);
    
    // Notify listeners
    this.notifyListeners('notification_added', notification);
    
    console.log('📱 Smart notification sent:', notification.title);
  }

  /**
   * Determines if a notification should be sent based on context and fatigue
   */
  private async shouldSendNotification(
    notification: InAppNotification
  ): Promise<{ shouldSend: boolean; reason: string; alternativeTime?: Date }> {
    // Get notification settings from localStorage
    const settings = this.getNotificationSettings();

    // Check if notifications are disabled
    if (!settings.enabled) {
      return {
        shouldSend: false,
        reason: 'Notifications are disabled',
      };
    }

    // Check quiet hours
    if (settings.quietHours.enabled && this.isInQuietHours(settings.quietHours)) {
      return {
        shouldSend: false,
        reason: 'Currently in quiet hours',
        alternativeTime: this.getNextAppropriateTime(settings.quietHours)
      };
    }

    // Check for duplicate notifications
    const duplicateCheck = this.checkForDuplicateNotification(notification);
    if (!duplicateCheck.shouldSend) {
      return duplicateCheck;
    }

    return { shouldSend: true, reason: 'All checks passed' };
  }

  /**
   * Checks for duplicate notifications based on content, schedule, and timing
   */
  private checkForDuplicateNotification(newNotification: InAppNotification): { shouldSend: boolean; reason: string } {
    const now = new Date();
    const recentThreshold = 10 * 1000; // 10 seconds
    
    // Check for exact duplicate based on scheduleId, title, and recent timing
    const exactDuplicate = this.activeNotifications.find(existing => 
      existing.scheduleId === newNotification.scheduleId &&
      existing.title === newNotification.title &&
      existing.type === newNotification.type &&
      (now.getTime() - existing.createdAt.getTime()) < recentThreshold
    );
    
    if (exactDuplicate) {
      console.log('🚫 Blocking exact duplicate notification:', {
        scheduleId: newNotification.scheduleId,
        title: newNotification.title,
        existingId: exactDuplicate.id,
        timeSinceExisting: now.getTime() - exactDuplicate.createdAt.getTime()
      });
      return {
        shouldSend: false,
        reason: `Duplicate notification blocked - similar notification sent ${Math.round((now.getTime() - exactDuplicate.createdAt.getTime()) / 1000)}s ago`
      };
    }
    
    // Check for content-based duplicates (same title and message within recent time)
    const contentDuplicate = this.activeNotifications.find(existing =>
      existing.title === newNotification.title &&
      existing.message === newNotification.message &&
      existing.type === newNotification.type &&
      (now.getTime() - existing.createdAt.getTime()) < recentThreshold
    );
    
    if (contentDuplicate) {
      console.log('🚫 Blocking content duplicate notification:', {
        title: newNotification.title,
        existingId: contentDuplicate.id,
        timeSinceExisting: now.getTime() - contentDuplicate.createdAt.getTime()
      });
      return {
        shouldSend: false,
        reason: `Duplicate content blocked - similar notification sent ${Math.round((now.getTime() - contentDuplicate.createdAt.getTime()) / 1000)}s ago`
      };
    }
    
    // Check for schedule-based duplicates with same list (allow only one active notification per schedule+list combination)
    const scheduleDuplicate = this.activeNotifications.find(existing =>
      existing.scheduleId.includes('auto_') && newNotification.scheduleId.includes('auto_') &&
      existing.scheduleId.split('_')[1] === newNotification.scheduleId.split('_')[1] && // Same list ID
      existing.type === 'schedule_reminder' && newNotification.type === 'schedule_reminder' &&
      (now.getTime() - existing.createdAt.getTime()) < (60 * 1000) // Within 1 minute
    );
    
    if (scheduleDuplicate) {
      console.log('🚫 Blocking schedule duplicate notification:', {
        newScheduleId: newNotification.scheduleId,
        existingScheduleId: scheduleDuplicate.scheduleId,
        existingId: scheduleDuplicate.id,
        timeSinceExisting: now.getTime() - scheduleDuplicate.createdAt.getTime()
      });
      return {
        shouldSend: false,
        reason: `Schedule duplicate blocked - notification for same list sent ${Math.round((now.getTime() - scheduleDuplicate.createdAt.getTime()) / 1000)}s ago`
      };
    }
    
    return { shouldSend: true, reason: 'No duplicates found' };
  }

  /**
   * Sends browser push notification
   */
  private async sendBrowserNotification(notification: InAppNotification): Promise<void> {
    // Check if browser supports notifications
    if (!('Notification' in window)) {
      console.log('Browser does not support notifications');
      return;
    }

    // Check if permission is granted
    if (Notification.permission !== 'granted') {
      console.log('Notification permission not granted');
      return;
    }

    const settings = this.getNotificationSettings();

    try {
      // Use ServiceWorkerRegistration.showNotification for actions support
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.ready;
        // Get browser-compatible actions (limit to 2 most important)
        const browserActions = this.getBrowserActions(notification);
        
        await registration.showNotification(notification.title, {
          body: notification.message,
          icon: '/icons/icon-192x192.png',
          badge: '/icons/icon-96x96.png',
          tag: notification.id,
          requireInteraction: notification.priority === 'high' || notification.priority === 'critical',
          silent: !settings.soundEnabled,
          actions: browserActions as any,
          data: {
            notificationId: notification.id,
            scheduleId: notification.scheduleId,
            listId: this.extractListIdFromScheduleIdSync(notification.scheduleId),
            type: notification.type,
            actions: notification.actions
          }
        } as any);
      } else {
        // Fallback to regular notification without actions
        const browserNotification = new Notification(notification.title, {
          body: notification.message,
          icon: '/icons/icon-192x192.png',
          badge: '/icons/icon-96x96.png',
          tag: notification.id,
          requireInteraction: notification.priority === 'high' || notification.priority === 'critical',
          silent: !settings.soundEnabled,
          data: {
            notificationId: notification.id,
            scheduleId: notification.scheduleId,
            listId: this.extractListIdFromScheduleIdSync(notification.scheduleId),
            actions: notification.actions
          }
        });

        // Handle browser notification click for fallback
        browserNotification.onclick = () => {
          console.log('Browser notification clicked:', notification.id);
          
          // Focus the window if it exists
          if (window.focus) {
            window.focus();
          }
          
          // Mark as read
          this.markAsRead(notification.id);
          
          // Close the notification
          browserNotification.close();
          
          // Notify listeners to potentially navigate
          this.notifyListeners('notification_clicked', {
            notificationId: notification.id,
            scheduleId: notification.scheduleId
          });
        };
      }


      // Trigger vibration if enabled and supported
      if (settings.vibrationEnabled && 'vibrate' in navigator) {
        const vibrationPattern = this.getVibrationPattern(notification.priority);
        navigator.vibrate(vibrationPattern);
      }


      console.log('🔔 Browser notification sent:', notification.title);

    } catch (error) {
      console.error('Failed to send browser notification:', error);
    }
  }


  /**
   * Gets vibration pattern based on priority
   */
  private getVibrationPattern(priority: string): number[] {
    switch (priority) {
      case 'critical':
        return [200, 100, 200, 100, 200]; // Urgent pattern
      case 'high':
        return [200, 100, 200]; // Important pattern
      case 'medium':
        return [200]; // Single vibration
      default:
        return [100]; // Gentle vibration
    }
  }

  /**
   * Handles user actions on notifications
   */
  async handleNotificationAction(
    notificationId: string,
    action: string,
    data?: any
  ): Promise<void> {
    console.log(`🚀 handleNotificationAction called with:`, { notificationId, action, data });
    console.log(`📋 Current active notifications:`, this.activeNotifications.map(n => ({ id: n.id, title: n.title })));

    let notification = this.activeNotifications.find(n => n.id === notificationId);

    // If notification not in memory (common on mobile when app was backgrounded)
    if (!notification) {
      console.warn('⚠️ Notification not found in activeNotifications (likely from push notification while app was backgrounded):', notificationId);

      // Try to load from localStorage storage first
      notification = await this.loadNotificationFromStorage(notificationId);

      // If still not found but we have scheduleId in data (from service worker),
      // we can still process the complete action
      if (!notification && data?.scheduleId) {
        console.log('📦 Processing push notification action using scheduleId fallback:', data.scheduleId);

        // For 'complete' action, we can proceed with just the scheduleId
        if (action === 'complete') {
          await this.handleCompleteActionFromScheduleId(data.scheduleId);
          return;
        }

        // For other actions that require the full notification, we cannot proceed
        console.error('❌ Cannot process action without notification:', action);
        return;
      }

      // If we still can't find or reconstruct the notification, log and return
      if (!notification) {
        console.error('❌ Cannot process action: notification not found and no scheduleId fallback available');
        return;
      }

      console.log('✅ Recovered notification from storage:', notification.title);
    }

    console.log(`🎯 Handling notification action: ${action} for ${notification.title}`);

    switch (action) {
      case 'complete':
        console.log('✅ Processing complete action...');
        await this.handleCompleteAction(notification);
        break;
      case 'snooze':
        console.log('⏰ Processing snooze action...');
        await this.handleSnoozeAction(notification, data?.minutes || 15);
        break;
      case 'reschedule':
        console.log('📅 Processing reschedule action...');
        await this.handleRescheduleAction(notification);
        break;
      case 'dismiss':
        console.log('❌ Processing dismiss action...');
        await this.handleDismissAction(notification);
        break;
      case 'navigate':
        console.log('🧭 Processing navigate action...');
        await this.handleNavigateAction(notification, data);
        break;
      default:
        console.warn('❓ Unknown action:', action);
    }

    // Mark as read and record response
    console.log('📝 Marking notification as read and updating storage...');
    notification.isRead = true;
    await this.updateNotificationInStorage(notification);
    await this.recordNotificationResponse(notificationId, action, data);
    
    // Remove from active notifications unless it's a snooze
    if (action !== 'snooze') {
      console.log('🗑️ Removing notification from active list...');
      this.removeNotification(notificationId);
    }

    console.log('📢 Notifying listeners of notification update...');
    this.notifyListeners('notification_updated', notification);
    console.log('✅ Notification action handling completed');
  }

  // === NOTIFICATION ACTIONS ===

  private async handleCompleteAction(notification: InAppNotification): Promise<void> {
    console.log('🎯 handleCompleteAction called for notification:', notification.title);
    
    try {
      const listId = await this.getListIdFromSchedule(notification.scheduleId);
      console.log('📋 Got listId from schedule:', listId, 'from scheduleId:', notification.scheduleId);
      
      // Validate list exists before proceeding
      const listExists = await this.validateListExists(listId);
      if (!listExists) {
        console.error('❌ Cannot complete action: list does not exist:', listId);
        // Show error notification to user
        await this.sendScheduleNotification(
          `error_${Date.now()}`,
          'system',
          '❌ Error',
          'Could not find the list for this notification. Please check your data.',
          'schedule_reminder',
          { priority: 'high', confidence: 1.0 }
        );
        return;
      }
      
      // Complete the next item in the list and get the following item
      console.log('📢 Notifying listeners to complete next item...');
      this.notifyListeners('complete_next_item', {
        scheduleId: notification.scheduleId,
        listId: listId
      });
      console.log('✅ complete_next_item event dispatched');
      
      // Record positive engagement
      await this.recordPositiveEngagement(notification.id);
      console.log('👍 Positive engagement recorded');
      
      // Schedule next item notification after a short delay
      console.log('⏰ Scheduling next item notification in 2 seconds...');
      setTimeout(async () => {
        console.log('🔔 Sending next item notification now...');
        await this.sendNextItemNotification(notification.scheduleId, listId);
      }, 2000); // 2 second delay to allow UI updates
      
      console.log('✅ handleCompleteAction completed');
    } catch (error) {
      console.error('❌ Failed to handle complete action:', error);
      // Show error notification
      await this.sendScheduleNotification(
        `error_${Date.now()}`,
        'system',
        '❌ Error',
        'Failed to complete the item. Please try again.',
        'schedule_reminder',
        { priority: 'medium', confidence: 1.0 }
      );
    }
  }

  /**
   * Handles complete action when notification is not in memory.
   * This is specifically for push notification actions when the app was backgrounded/closed.
   * The service worker provides the scheduleId, which is sufficient to complete the action.
   */
  private async handleCompleteActionFromScheduleId(scheduleId: string): Promise<void> {
    console.log('🎯 handleCompleteActionFromScheduleId (push notification recovery) called with:', scheduleId);

    try {
      const listId = await this.getListIdFromSchedule(scheduleId);
      console.log('📋 Got listId from schedule:', listId);

      // Validate list exists before proceeding
      const listExists = await this.validateListExists(listId);
      if (!listExists) {
        console.error('❌ Cannot complete action: list does not exist:', listId);
        await this.sendScheduleNotification(
          `error_${Date.now()}`,
          'system',
          '❌ Error',
          'Could not find the list for this notification. Please check your data.',
          'schedule_reminder',
          { priority: 'high', confidence: 1.0 }
        );
        return;
      }

      // Complete the next item in the list (same as handleCompleteAction)
      console.log('📢 Notifying listeners to complete next item...');
      this.notifyListeners('complete_next_item', {
        scheduleId: scheduleId,
        listId: listId
      });
      console.log('✅ complete_next_item event dispatched');

      // Record positive engagement
      await this.recordPositiveEngagement(`push_${scheduleId}`);
      console.log('👍 Positive engagement recorded for push notification action');

      // Schedule next item notification after a short delay
      console.log('⏰ Scheduling next item notification in 2 seconds...');
      setTimeout(async () => {
        console.log('🔔 Sending next item notification now...');
        await this.sendNextItemNotification(scheduleId, listId);
      }, 2000);

      console.log('✅ Push notification complete action handled successfully');
    } catch (error) {
      console.error('❌ Failed to handle push notification complete action:', error);
      await this.sendScheduleNotification(
        `error_${Date.now()}`,
        'system',
        '❌ Error',
        'Failed to complete the item. Please try again.',
        'schedule_reminder',
        { priority: 'medium', confidence: 1.0 }
      );
    }
  }

  /**
   * Sends notification for the next incomplete item in a list
   */
  private completedListNotifications = new Set<string>();
  
  private async sendNextItemNotification(scheduleId: string, listId: string): Promise<void> {
    try {
      console.log('🔔 sendNextItemNotification called:', { scheduleId, listId });
      
      // Validate list exists first
      const listExists = await this.validateListExists(listId);
      if (!listExists) {
        console.error('❌ Cannot send notification: list does not exist:', listId);
        return;
      }
      
      // Get the next incomplete item from lists service
      const { listsService } = await import('../listsService');
      const nextItem = listsService.getNextIncompleteItem(listId);
      const list = listsService.getListById(listId);
      const listTitle = list?.title || 'Your List';
      
      // Get completion stats for debugging
      const listData = listsService.getListDataForContext(listId, new Date());
      console.log('📊 List stats:', {
        listId,
        listTitle,
        totalItems: listData?.totalItems || 0,
        completedItems: listData?.completedItems || 0,
        nextItemContent: nextItem?.content || 'None'
      });
      
      if (nextItem) {
        // Create new auto schedule ID for this notification
        const newScheduleId = `auto_${listId}_${Date.now()}`;
        
        // Send notification for the next item
        await this.sendScheduleNotification(
          newScheduleId,
          listId,
          `Next: ${nextItem.content}`,
          `Continue with "${listTitle}" - Complete your next task to stay on track!`,
          'schedule_reminder',
          {
            priority: 'high',
            confidence: 0.9,
            contextFactors: [
              'List is in progress',
              'High priority list',
              'Daily routine pending',
              `Scheduled for ${new Date().toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase()}`,
              'Next logical task in sequence',
              'auto_triggered'
            ]
          }
        );
        
        console.log('📬 Next item notification sent:', nextItem.content);
      } else {
        // Validate this is actually a completion, not a data error
        const hasItems = list?.items.length || 0;
        
        if (hasItems === 0) {
          console.warn('⚠️ List has no items, cannot determine completion status');
          return;
        }
        
        // Check if we've already sent a completion notification for this list today
        const today = new Date().toDateString();
        const completionKey = `${listId}_${today}`;
        
        if (this.completedListNotifications.has(completionKey)) {
          console.log('⚠️ Already sent completion notification for this list today, skipping...');
          await this.checkAndNotifyNextScheduledList(scheduleId);
          return;
        }
        
        // All items completed - send completion notification
        this.completedListNotifications.add(completionKey);
        
        await this.sendScheduleNotification(
          scheduleId,
          listId,
          `🎉 List Complete!`,
          `Congratulations! You've completed all items in "${listTitle}". Great job staying focused!`,
          'list_complete',
          {
            priority: 'high',
            confidence: 1.0,
            contextFactors: ['list_completed', 'all_items_done']
          }
        );
        
        console.log('🎉 List completion notification sent for:', listTitle);
        
        // Check for and notify about the next scheduled list
        console.log('🔍 Checking for next scheduled list after completing:', listTitle);
        await this.checkAndNotifyNextScheduledList(scheduleId);
      }
    } catch (error) {
      console.error('❌ Failed to send next item notification:', error);
    }
  }

  /**
   * Checks for the next scheduled list and sends a notification if one is found
   */
  private async checkAndNotifyNextScheduledList(completedScheduleId: string): Promise<void> {
    try {
      console.log('🎯 Checking for next scheduled list after completing schedule:', completedScheduleId);
      
      // Get the completed list ID to exclude it
      const completedListId = await this.getListIdFromSchedule(completedScheduleId);
      console.log('📋 Completed list ID:', completedListId);
      
      // Import necessary services
      const { calendarService } = await import('../calendarService');
      const { listsService } = await import('../listsService');
      
      // Get all active schedules
      const allSchedules = calendarService.getAllSchedules().filter(s => s.isActive);
      console.log('📅 Active schedules found:', allSchedules.map(s => ({ id: s.id, title: s.title, listId: s.listId })));
      
      // Find other scheduled lists for today (excluding the completed one)
      const now = new Date();
      const currentDay = now.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
      
      let nextList = null;
      let nextSchedule = null;
      
      for (const schedule of allSchedules) {
        // Skip the completed list
        if (schedule.listId === completedListId) {
          console.log(`⏭️ Skipping completed list: ${schedule.title}`);
          continue;
        }
        
        // Check if this schedule is active today
        if (schedule.recurringPattern?.daysOfWeek?.includes(currentDay as any)) {
          const list = listsService.getListById(schedule.listId);
          if (list) {
            // Check if this list has incomplete items
            const { universalCompletionService } = await import('../universalCompletionService');
            const listData = universalCompletionService.getListDataForContext(list, new Date());
            const hasIncompleteItems = listData ? listData.items.some(item => !item.completed) : list.items.length > 0;
            if (hasIncompleteItems) {
              console.log(`✅ Found scheduled list for today with incomplete items: ${list.title}`);
              nextList = list;
              nextSchedule = schedule;
              break;
            } else {
              console.log(`⏭️ Skipping list with all items completed: ${list.title}`);
            }
          }
        }
      }
      
      if (nextList && nextSchedule) {
        console.log('✨ Found next scheduled list:', nextList.title);
        
        // Create a new schedule ID for the next list notification
        const nextScheduleId = nextSchedule.id;
        
        // Send notification for the next scheduled list with a slight delay
        setTimeout(async () => {
          // Get the first incomplete item from the next list
          const nextItem = listsService.getNextIncompleteItem(nextList.id);
          
          if (nextItem) {
            await this.sendScheduleNotification(
              nextScheduleId,
              nextList.id,
              `Next Up: ${nextList.title}`,
              `Great job! Ready to continue with "${nextList.title}"? First task: ${nextItem.content}`,
              'schedule_reminder',
              {
                priority: 'medium',
                confidence: 0.9,
                contextFactors: ['previous_list_completed', 'next_scheduled_list', `scheduled_for_${currentDay}`]
              }
            );
            
            console.log('📬 Next scheduled list notification sent:', nextList.title);
          }
        }, 3000); // 3 second delay to allow UI to update
      } else {
        console.log('ℹ️ No other scheduled lists with incomplete items found for today');
      }
    } catch (error) {
      console.error('Failed to check for next scheduled list:', error);
    }
  }

  private async handleSnoozeAction(notification: InAppNotification, minutes: number): Promise<void> {
    // Create a snoozed notification
    const snoozeTime = new Date(Date.now() + minutes * 60 * 1000);
    const snoozedNotification: InAppNotification = {
      ...notification,
      id: `${notification.id}_snooze_${Date.now()}`,
      triggerTime: snoozeTime,
      message: `⏰ Snoozed: ${notification.message}`,
      createdAt: new Date(),
      isRead: false
    };

    // Schedule the snoozed notification
    setTimeout(() => {
      this.activeNotifications.push(snoozedNotification);
      this.notifyListeners('notification_added', snoozedNotification);
    }, minutes * 60 * 1000);

    console.log(`⏰ Notification snoozed for ${minutes} minutes`);
  }

  private async handleRescheduleAction(notification: InAppNotification): Promise<void> {
    // Open reschedule dialog
    this.notifyListeners('open_reschedule_dialog', {
      scheduleId: notification.scheduleId,
      notificationId: notification.id
    });
  }

  private async handleDismissAction(_notification: InAppNotification): Promise<void> {
    // Just record the dismissal - notification will be removed by caller
    console.log('❌ Notification dismissed');
  }

  private async handleNavigateAction(notification: InAppNotification, data?: any): Promise<void> {
    // Navigate to the specified location
    const listId = data?.listId || await this.getListIdFromSchedule(notification.scheduleId);
    
    this.notifyListeners('navigate_to_list', {
      scheduleId: notification.scheduleId,
      listId: listId
    });
    
    console.log('🧭 Navigation requested to list:', listId);
  }

  // === NOTIFICATION QUEUE MANAGEMENT ===

  /**
   * Gets all active notifications
   */
  getActiveNotifications(): InAppNotification[] {
    return [...this.activeNotifications].sort((a, b) => 
      b.createdAt.getTime() - a.createdAt.getTime()
    );
  }

  /**
   * Gets unread notifications
   */
  getUnreadNotifications(): InAppNotification[] {
    return this.activeNotifications.filter(n => !n.isRead);
  }

  /**
   * Marks a notification as read
   */
  async markAsRead(notificationId: string): Promise<void> {
    const notification = this.activeNotifications.find(n => n.id === notificationId);
    if (notification && !notification.isRead) {
      notification.isRead = true;
      await this.updateNotificationInStorage(notification);
      this.notifyListeners('notification_updated', notification);
    }
  }

  /**
   * Removes a notification from the active list
   */
  removeNotification(notificationId: string): void {
    const index = this.activeNotifications.findIndex(n => n.id === notificationId);
    if (index !== -1) {
      this.activeNotifications.splice(index, 1);
      this.notifyListeners('notification_removed', { notificationId });
    }
  }

  /**
   * Clears expired notifications
   */
  private cleanupExpiredNotifications(): void {
    const now = new Date();
    const fiveMinutesAgo = new Date(now.getTime() - 5 * 60 * 1000);
    
    // Clean up expired notifications
    const expired = this.activeNotifications.filter(n => n.expiresAt <= now);
    
    // Clean up old read notifications (older than 5 minutes)
    const oldRead = this.activeNotifications.filter(n => 
      n.isRead && n.createdAt <= fiveMinutesAgo
    );
    
    // Clean up old schedule_reminder notifications that are read (to prevent buildup)
    const oldScheduleReminders = this.activeNotifications.filter(n => 
      n.type === 'schedule_reminder' && 
      n.isRead && 
      (now.getTime() - n.createdAt.getTime()) > (2 * 60 * 1000) // 2 minutes old
    );
    
    const toRemove = [...expired, ...oldRead, ...oldScheduleReminders];
    const uniqueToRemove = Array.from(new Set(toRemove.map(n => n.id)))
      .map(id => toRemove.find(n => n.id === id))
      .filter(Boolean);
    
    uniqueToRemove.forEach(notification => {
      this.removeNotification(notification!.id);
    });

    if (uniqueToRemove.length > 0) {
      console.log(`🧹 Cleaned up ${uniqueToRemove.length} notifications (${expired.length} expired, ${oldRead.length} old read, ${oldScheduleReminders.length} old schedule reminders)`);
    }
  }

  // === UTILITY METHODS ===

  /**
   * Gets notification settings from localStorage with fallback defaults
   */
  private getNotificationSettings(): any {
    try {
      const settings = localStorage.getItem('godmode_notification_settings');
      if (settings) {
        return JSON.parse(settings);
      }
    } catch (error) {
      console.error('Failed to load notification settings:', error);
    }

    // Fallback to reasonable defaults
    return {
      enabled: true,
      soundEnabled: true,
      vibrationEnabled: true,
      quietHours: {
        enabled: false,
        start: '22:00',
        end: '07:00'
      }
    };
  }

  /**
   * Checks if current time is within quiet hours
   */
  private isInQuietHours(quietHours: { start: string; end: string }): boolean {
    const now = new Date();
    const currentTime = now.getHours() * 60 + now.getMinutes();
    
    const [startHour, startMin] = quietHours.start.split(':').map(Number);
    const [endHour, endMin] = quietHours.end.split(':').map(Number);
    
    const startTime = startHour * 60 + startMin;
    const endTime = endHour * 60 + endMin;

    // Handle quiet hours that span midnight
    if (startTime > endTime) {
      return currentTime >= startTime || currentTime <= endTime;
    } else {
      return currentTime >= startTime && currentTime <= endTime;
    }
  }

  /**
   * Gets the next appropriate time for notifications based on quiet hours
   */
  private getNextAppropriateTime(quietHours: { start: string; end: string }): Date {
    const now = new Date();
    const nextTime = new Date(now);
    
    const [endHour, endMin] = quietHours.end.split(':').map(Number);
    
    // If we're in quiet hours, schedule for when quiet hours end
    if (this.isInQuietHours(quietHours)) {
      // If quiet hours end tomorrow (e.g., 22:00-07:00)
      if (quietHours.start > quietHours.end && now.getHours() >= parseInt(quietHours.start.split(':')[0])) {
        nextTime.setDate(now.getDate() + 1);
      }
      nextTime.setHours(endHour, endMin, 0, 0);
    }
    // Otherwise, schedule for next hour
    else {
      nextTime.setHours(now.getHours() + 1, 0, 0, 0);
    }
    
    return nextTime;
  }

  /**
   * Get browser-compatible actions (limit to 2 most important)
   */
  private getBrowserActions(notification: InAppNotification): any[] {
    const config = getNotificationConfig(notification.type);
    
    // Get primary and danger actions first, then secondary
    const priorityActions = config.defaultActions
      .filter(a => a.variant === 'primary' || a.variant === 'danger')
      .slice(0, 2);
    
    let actions = priorityActions.map(a => ({
      action: a.action,
      title: a.label
    }));
    
    // If we have space and no dismiss action, add dismiss
    if (actions.length < 2 && !actions.find(a => a.action === 'dismiss')) {
      const dismissAction = config.defaultActions.find(a => a.action === 'dismiss');
      if (dismissAction) {
        actions.push({
          action: 'dismiss',
          title: dismissAction.label
        });
      }
    }
    
    return actions;
  }

  // === SPECIALIZED NOTIFICATION METHODS ===

  /**
   * Send list completion notification
   */
  async sendListCompleteNotification(listId: string, listTitle: string, itemsCompleted: number): Promise<void> {
    await this.sendScheduleNotification(
      '', // No schedule ID for completion notifications
      listId,
      `🎉 ${listTitle} Complete!`,
      `Congratulations! You've completed all ${itemsCompleted} items.`,
      'list_complete',
      {
        priority: 'low',
        confidence: 1.0,
        contextFactors: ['list_completed', 'achievement']
      }
    );
  }

  /**
   * Send deadline warning notification
   */
  async sendDeadlineWarning(
    scheduleId: string, 
    listId: string, 
    listTitle: string, 
    deadline: Date
  ): Promise<void> {
    const hoursUntilDeadline = Math.round((deadline.getTime() - Date.now()) / (1000 * 60 * 60));
    
    await this.sendScheduleNotification(
      scheduleId,
      listId,
      `⚠️ Deadline Approaching: ${listTitle}`,
      `Due in ${hoursUntilDeadline} hours. ${hoursUntilDeadline <= 2 ? 'Urgent action required!' : 'Plan your time accordingly.'}`,
      'deadline_warning',
      {
        priority: hoursUntilDeadline <= 2 ? 'critical' : 'high',
        confidence: 1.0,
        contextFactors: ['deadline', `${hoursUntilDeadline}h_remaining`]
      }
    );
  }

  /**
   * Send break reminder notification
   */
  async sendBreakReminder(): Promise<void> {
    await this.sendScheduleNotification(
      '',
      '',
      '☕ Time for a Break',
      'You\'ve been working for a while. Take a 5-minute break to stay productive.',
      'break_reminder',
      {
        priority: 'low',
        confidence: 0.8,
        contextFactors: ['break_time', 'productivity']
      }
    );
  }

  /**
   * Send item completion notification
   */
  async sendItemCompleteNotification(
    listId: string,
    itemTitle: string,
    remainingItems: number
  ): Promise<void> {
    await this.sendScheduleNotification(
      '',
      listId,
      '✅ Item Complete',
      `"${itemTitle}" done! ${remainingItems > 0 ? `${remainingItems} items remaining.` : 'All items complete!'}`,
      'item_complete',
      {
        priority: 'low',
        confidence: 1.0,
        contextFactors: ['item_completed', `${remainingItems}_remaining`]
      }
    );
  }

  /**
   * Send daily summary notification
   */
  async sendDailySummary(
    completedTasks: number,
    upcomingTasks: number,
    streakDays: number
  ): Promise<void> {
    await this.sendScheduleNotification(
      '',
      '',
      '📊 Daily Summary',
      `Completed: ${completedTasks} tasks | Upcoming: ${upcomingTasks} tasks | Streak: ${streakDays} days`,
      'daily_summary',
      {
        priority: 'low',
        confidence: 1.0,
        contextFactors: ['daily_summary', 'end_of_day']
      }
    );
  }

  // === UTILITY METHODS ===

  /**
   * Gets icon for notification type
   */
  private getIconForType(type: NotificationType): InAppNotification['iconType'] {
    const iconMap: Record<string, InAppNotification['iconType']> = {
      'schedule_reminder': 'clock',
      'deadline_warning': 'alert',
      'task_suggestion': 'lightbulb',
      'break_reminder': 'coffee',
      'list_complete': 'check-circle',
      'item_complete': 'check',
      'daily_summary': 'bar-chart'
    };
    return iconMap[type] || 'bell';
  }

  /**
   * Gets color for notification type
   */
  private getColorForType(type: NotificationType): InAppNotification['color'] {
    const config = getNotificationConfig(type);
    
    switch (config.category) {
      case NotificationCategory.WARNING:
        return 'red';
      case NotificationCategory.ACHIEVEMENT:
        return 'green';
      case NotificationCategory.INFORMATION:
        return 'blue';
      case NotificationCategory.REMINDER:
        return 'yellow';
      case NotificationCategory.TASK_ACTION:
        return 'purple';
      default:
        return 'blue';
    }
  }

  /**
   * Gets default actions for notifications (legacy - kept for compatibility)
   */
  private getDefaultActions(): NotificationAction[] {
    return getNotificationConfig('schedule_reminder').defaultActions;
  }


  private generateNotificationId(): string {
    return `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // === DATA PERSISTENCE ===

  private async saveNotificationToStorage(notification: InAppNotification): Promise<void> {
    try {
      const stored = JSON.parse(localStorage.getItem('godmode_notifications') || '[]');
      stored.push(notification);
      
      // Keep only last 100 notifications
      if (stored.length > 100) {
        stored.splice(0, stored.length - 100);
      }
      
      localStorage.setItem('godmode_notifications', JSON.stringify(stored));
    } catch (error) {
      console.error('Failed to save notification to storage:', error);
    }
  }

  private async updateNotificationInStorage(notification: InAppNotification): Promise<void> {
    try {
      const stored = JSON.parse(localStorage.getItem('godmode_notifications') || '[]');
      const index = stored.findIndex((n: InAppNotification) => n.id === notification.id);

      if (index !== -1) {
        stored[index] = notification;
        localStorage.setItem('godmode_notifications', JSON.stringify(stored));
      }
    } catch (error) {
      console.error('Failed to update notification in storage:', error);
    }
  }

  /**
   * Loads a notification from localStorage storage.
   * Used to recover notification data when the app was backgrounded and activeNotifications is empty.
   */
  private async loadNotificationFromStorage(notificationId: string): Promise<InAppNotification | undefined> {
    try {
      const stored = JSON.parse(localStorage.getItem('godmode_notifications') || '[]');
      const notification = stored.find((n: any) => n.id === notificationId);

      if (notification) {
        console.log('✅ Recovered notification from localStorage:', notificationId);
        // Convert date strings back to Date objects
        return {
          ...notification,
          triggerTime: new Date(notification.triggerTime),
          createdAt: new Date(notification.createdAt),
          expiresAt: new Date(notification.expiresAt),
        };
      }
    } catch (error) {
      console.error('Failed to load notification from storage:', error);
    }
    return undefined;
  }

  private async recordNotificationResponse(
    notificationId: string, 
    action: string, 
    data?: any
  ): Promise<void> {
    // This would record user responses for learning
    // For now, just log
    console.log('📊 Notification response recorded:', { notificationId, action, data });
  }

  private async recordPositiveEngagement(notificationId: string): Promise<void> {
    // Record that user positively engaged with notification
    console.log('👍 Positive engagement recorded for:', notificationId);
  }

  // === LISTENER MANAGEMENT ===

  addNotificationListener(listener: (event: string, data: any) => void): void {
    this.listeners.add(listener);
  }

  removeNotificationListener(listener: (event: string, data: any) => void): void {
    this.listeners.delete(listener);
  }

  private notifyListeners(event: string, data: any): void {
    this.listeners.forEach(listener => {
      try {
        listener(event, data);
      } catch (error) {
        console.error('Error in notification listener:', error);
      }
    });
  }

  // === BACKGROUND PROCESSING ===

  private startNotificationProcessor(): void {
    // Run cleanup every 5 minutes
    setInterval(() => {
      this.cleanupExpiredNotifications();
    }, 5 * 60 * 1000);

    // Process pending notifications every minute
    setInterval(() => {
      this.processPendingNotifications();
    }, 60 * 1000);
  }

  private processPendingNotifications(): void {
    // This would process scheduled notifications
    // For now, just cleanup
    this.cleanupExpiredNotifications();
  }

  // === HELPER METHODS ===

  /**
   * Synchronously extracts listId from scheduleId without async imports.
   * Used when creating notification data for the service worker.
   * Returns null if the format is not recognized.
   */
  private extractListIdFromScheduleIdSync(scheduleId: string): string | null {
    // Handle auto-generated schedule IDs: auto_[listId]_[timestamp]
    if (scheduleId.startsWith('auto_')) {
      const parts = scheduleId.split('_');
      if (parts.length >= 3) {
        // Remove 'auto' prefix and timestamp suffix, join the middle parts
        return parts.slice(1, -1).join('_');
      }
    }

    // Handle regular schedule IDs: schedule_[listId] or schedule_[listId]_[timestamp]
    if (scheduleId.startsWith('schedule_')) {
      const withoutPrefix = scheduleId.replace('schedule_', '');
      const parts = withoutPrefix.split('_');
      // If there's a timestamp at the end (numeric), remove it
      if (parts.length >= 2 && /^\d+$/.test(parts[parts.length - 1])) {
        return parts.slice(0, -1).join('_');
      }
      return withoutPrefix;
    }

    // For other formats, return null (the async method will handle it)
    return null;
  }

  /**
   * Validates that schedule data is consistent between services
   * This helps debug issues with data synchronization
   */
  private async validateScheduleDataConsistency(): Promise<void> {
    try {
      const { calendarService } = await import('../calendarService');
      const schedules = calendarService.getAllSchedules();
      
      console.log('🔍 SmartNotificationService validation:');
      console.log(`📊 Found ${schedules.length} schedules in localStorage via calendarService`);
      console.log('📋 Schedule IDs:', schedules.map(s => s.id));
      console.log('📋 List mappings:', schedules.map(s => `${s.id} → ${s.listId}`));
      
      // Also check raw localStorage for comparison
      const rawSchedules = localStorage.getItem('godmode_list_schedules');
      if (rawSchedules) {
        const parsed = JSON.parse(rawSchedules);
        console.log(`📊 Raw localStorage has ${parsed.length} schedules`);
      }
    } catch (error) {
      console.error('❌ Schedule data validation failed:', error);
    }
  }

  private async getListIdFromSchedule(scheduleId: string): Promise<string> {
    console.log('🔍 Resolving listId for scheduleId:', scheduleId);
    
    // First, try to find in actual schedules from localStorage
    const { calendarService } = await import('../calendarService');
    const schedules = calendarService.getAllSchedules();
    const schedule = schedules.find(s => s.id === scheduleId);
    
    if (schedule) {
      console.log('📋 Found schedule in localStorage:', schedule.id, '→', schedule.listId);
      return schedule.listId;
    }
    
    // Handle auto-generated schedule IDs
    if (scheduleId.startsWith('auto_')) {
      // Extract list ID from auto-generated schedule ID
      // Format: auto_[listId]_[timestamp]
      const parts = scheduleId.split('_');
      if (parts.length >= 3) {
        // Remove 'auto' and timestamp, join the middle parts
        const listIdParts = parts.slice(1, -1);
        const extractedListId = listIdParts.join('_');
        
        // Verify this list actually exists
        const { listsService } = await import('../listsService');
        const list = listsService.getListById(extractedListId);
        
        if (list) {
          console.log('📋 Extracted and verified listId from auto scheduleId:', scheduleId, '→', extractedListId);
          return extractedListId;
        } else {
          console.warn('⚠️ Extracted listId does not exist:', extractedListId);
        }
      }
    }
    
    // For regular schedule IDs, try to extract from format
    if (scheduleId.includes('_')) {
      const parts = scheduleId.split('_');
      if (parts.length >= 2) {
        if (parts[0] === 'schedule') {
          // Remove 'schedule' prefix, join the rest (no timestamp removal for regular schedules)
          const extractedListId = parts.slice(1).join('_');
          
          // Verify this list actually exists
          const { listsService } = await import('../listsService');
          const list = listsService.getListById(extractedListId);
          
          if (list) {
            console.log('📋 Extracted listId from regular scheduleId:', scheduleId, '→', extractedListId);
            return extractedListId;
          }
        }
      }
    }
    
    console.error('❌ Could not resolve listId for scheduleId:', scheduleId);
    throw new Error(`Could not resolve listId for scheduleId: ${scheduleId}`);
  }

  private async validateListExists(listId: string): Promise<boolean> {
    try {
      const { listsService } = await import('../listsService');
      const list = listsService.getListById(listId);
      const exists = !!list;
      
      if (!exists) {
        console.warn('⚠️ List does not exist:', listId);
      } else {
        console.log('✅ List exists:', listId, 'with', list.items.length, 'items');
      }
      
      return exists;
    } catch (error) {
      console.error('❌ Error validating list existence:', error);
      return false;
    }
  }

  /**
   * Checks if a schedule should be active at the given time
   */
  private isScheduleActiveNow(schedule: any, now: Date): boolean {
    if (!schedule.recurringPattern) {
      return true; // If no pattern, assume always active
    }

    const currentDay = now.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentTime = currentHour * 60 + currentMinute; // Convert to minutes

    // Check if today is a scheduled day
    const { recurringPattern } = schedule;
    if (recurringPattern.daysOfWeek && !recurringPattern.daysOfWeek.includes(currentDay)) {
      return false; // Not scheduled for today
    }

    // Check time proximity (show notifications within a reasonable window)
    if (recurringPattern.time) {
      const [scheduleHour, scheduleMinute] = recurringPattern.time.split(':').map(Number);
      const scheduleTime = scheduleHour * 60 + scheduleMinute;
      
      // For daily recurring tasks, be more flexible with timing
      if (schedule.type === 'daily') {
        // Daily tasks can be shown throughout the day if incomplete
        // This handles cases like morning routines scheduled at midnight
        // that should be available all day until completed
        const timeDifference = currentTime - scheduleTime;
        
        // Allow notifications:
        // - 2 hours before → rest of the day (up to 20 hours after)
        // This covers morning routines scheduled at 00:00 that should show all day
        const isWithinWindow = timeDifference >= -120 && timeDifference <= 1200; // 20 hours
        
        if (!isWithinWindow) {
          return false;
        }
      } else {
        // For weekly/monthly tasks, be flexible with morning routines
        const timeDifference = currentTime - scheduleTime;
        
        // Special handling for morning routines scheduled at 00:00
        if (scheduleHour === 0) {
          // Morning routines can be shown throughout the day (like daily tasks)
          const isWithinWindow = timeDifference >= -120 && timeDifference <= 1200; // 20 hours
          
          if (!isWithinWindow) {
            return false;
          }
        } else {
          // For other weekly tasks, use stricter timing (2 hours before → 2 hours after)
          const isWithinWindow = timeDifference >= -120 && timeDifference <= 120;
          
          if (!isWithinWindow) {
            return false;
          }
        }
      }
    }

    return true;
  }

  /**
   * Debug method to test schedule ID resolution
   * Available in browser console: window.smartNotificationService.debugScheduleResolution()
   */
  async debugScheduleResolution(scheduleId?: string): Promise<void> {
    if (!scheduleId) {
      // Test with all available schedule IDs
      const { calendarService } = await import('../calendarService');
      const schedules = calendarService.getAllSchedules();
      
      console.log('🧪 Testing schedule resolution for all schedules:');
      for (const schedule of schedules) {
        const resolvedListId = await this.getListIdFromSchedule(schedule.id);
        console.log(`✅ ${schedule.id} → ${resolvedListId} (expected: ${schedule.listId})`);
        
        if (resolvedListId !== schedule.listId) {
          console.error(`❌ Mismatch! Expected ${schedule.listId}, got ${resolvedListId}`);
        }
      }
    } else {
      // Test specific schedule ID
      const resolvedListId = await this.getListIdFromSchedule(scheduleId);
      console.log(`🧪 Schedule resolution test: ${scheduleId} → ${resolvedListId}`);
    }
  }

  /**
   * Debug method to validate entire notification completion flow
   */
  async debugNotificationCompletion(scheduleId?: string): Promise<void> {
    console.log('🧪 === NOTIFICATION COMPLETION DEBUG START ===');
    
    if (scheduleId) {
      console.log('🔍 Debugging specific scheduleId:', scheduleId);
      
      try {
        // Test schedule resolution
        const listId = await this.getListIdFromSchedule(scheduleId);
        console.log('✅ Schedule resolution successful:', scheduleId, '→', listId);
        
        // Test list existence
        const listExists = await this.validateListExists(listId);
        console.log('✅ List validation:', listId, 'exists =', listExists);
        
        if (listExists) {
          // Test item completion flow
          const { listsService } = await import('../listsService');
          const nextItem = listsService.getNextIncompleteItem(listId);
          console.log('✅ Next incomplete item:', nextItem ? nextItem.content : 'None (all complete)');
          
          // Test completion count
          const listData = listsService.getListDataForContext(listId, new Date());
          console.log('📊 List completion status:', {
            totalItems: listData?.totalItems || 0,
            completedItems: listData?.completedItems || 0,
            progress: listData?.progress || 0
          });
        }
        
      } catch (error) {
        console.error('❌ Debug failed:', error);
      }
    } else {
      // Debug all active notifications
      const notifications = this.getActiveNotifications();
      console.log('📋 Active notifications:', notifications.length);
      
      for (const notification of notifications.slice(0, 5)) { // Limit to first 5
        console.log('🔍 Debugging notification:', notification.id);
        await this.debugNotificationCompletion(notification.scheduleId);
      }
    }
    
    console.log('🧪 === NOTIFICATION COMPLETION DEBUG END ===');
  }

  /**
   * Debug method to identify and remove duplicate notifications
   */
  async debugAndCleanDuplicates(): Promise<void> {
    console.log('🧪 === DUPLICATE DETECTION AND CLEANUP START ===');
    
    const duplicates = [];
    const seen = new Map<string, InAppNotification>();
    
    for (const notification of this.activeNotifications) {
      const key = `${notification.title}|${notification.message}|${notification.type}`;
      
      if (seen.has(key)) {
        const existing = seen.get(key)!;
        duplicates.push({
          duplicate: notification,
          original: existing,
          timeDiff: notification.createdAt.getTime() - existing.createdAt.getTime()
        });
      } else {
        seen.set(key, notification);
      }
    }
    
    console.log(`🔍 Found ${duplicates.length} duplicate notifications:`, duplicates.map(d => ({
      duplicateId: d.duplicate.id,
      originalId: d.original.id,
      title: d.duplicate.title,
      timeDiffSeconds: Math.round(d.timeDiff / 1000)
    })));
    
    // Remove duplicates (keep the older one)
    for (const dup of duplicates) {
      console.log(`🗑️ Removing duplicate notification: ${dup.duplicate.id} (${dup.duplicate.title})`);
      this.removeNotification(dup.duplicate.id);
    }
    
    // Also run standard cleanup
    this.cleanupExpiredNotifications();
    
    console.log(`✅ Cleanup complete. Remaining notifications: ${this.activeNotifications.length}`);
    console.log('🧪 === DUPLICATE DETECTION AND CLEANUP END ===');
  }

  // === PREVIEW AND SCHEDULING ===

  /**
   * Gets the next notification that would be sent based on current schedules and context
   * @param excludeListId - Optional list ID to exclude from consideration (e.g., just completed list)
   */
  async getNextScheduledNotification(excludeListId?: string): Promise<{
    notification: Partial<InAppNotification>;
    scheduleInfo: {
      listId: string;
      listTitle: string;
      nextItem?: {
        content: string;
        description?: string;
        priority: number;
      };
      scheduledTime?: Date;
      confidence: number;
      reasoning: string[];
    };
  } | null> {
    try {
      // Get calendar service and lists service
      const { calendarService } = await import('../calendarService');
      const { listsService } = await import('../listsService');
      const { universalCompletionService } = await import('../universalCompletionService');
      
      // Get all active schedules
      const schedules = calendarService.getAllSchedules().filter(s => s.isActive);
      
      if (schedules.length === 0) {
        return null;
      }
      
      // Find the most relevant schedule based on time and context
      let bestSchedule = null;
      let bestScore = 0;
      const now = new Date();
      const currentDay = now.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
      const currentHour = now.getHours();
      const currentTime = currentHour * 60 + now.getMinutes(); // Convert to minutes
      
      for (const schedule of schedules) {
        const list = listsService.getListById(schedule.listId);
        if (!list) continue;
        
        // Skip if this is the excluded list (e.g., just completed)
        if (excludeListId && list.id === excludeListId) continue;
        
        const nextItem = listsService.getNextIncompleteItem(list.id);
        if (!nextItem) continue; // Skip if no incomplete items
        
        // Check if this schedule is relevant for today
        if (!this.isScheduleActiveNow(schedule, now)) {
          continue; // Skip schedules not active today
        }
        
        // Score based on various factors
        let score = 0;
        
        // Priority of the list
        score += (5 - list.priority) * 0.3; // Higher priority = higher score
        
        // Progress (lists with some progress but not complete score higher)
        const listData = universalCompletionService.getListDataForContext(list, new Date());
        const progress = listData ? listData.progress : 0;
        if (progress > 0 && progress < 100) {
          score += 0.4;
        }
        
        // Upcoming deadline
        if (list.deadline) {
          const daysUntilDeadline = Math.ceil((list.deadline.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
          if (daysUntilDeadline <= 7) {
            score += 0.3;
          }
        }
        
        // Status bonus
        if (list.status === 'in_progress') {
          score += 0.2;
        }
        
        // Time proximity bonus (closer to scheduled time = higher score)
        if (schedule.recurringPattern?.time) {
          const [scheduleHour, scheduleMinute] = schedule.recurringPattern.time.split(':').map(Number);
          const scheduleTime = scheduleHour * 60 + scheduleMinute;
          const timeDifference = Math.abs(currentTime - scheduleTime);
          
          if (schedule.type === 'daily') {
            // For daily tasks, give bonus if it's during reasonable hours
            // Morning routines (00:00) should get bonus during morning/day hours
            if (scheduleHour === 0 && currentHour >= 6 && currentHour <= 18) {
              score += 0.3; // Bonus for morning routine during day hours
            } else if (timeDifference <= 60) {
              score += 0.5; // High bonus for exact time match
            } else if (timeDifference <= 120) {
              score += 0.2; // Medium bonus for nearby time
            }
          } else {
            // For weekly/monthly tasks, special handling for morning routines
            if (scheduleHour === 0 && currentHour >= 4 && currentHour <= 18) {
              score += 0.3; // Bonus for morning routine during day hours (starting at 4 AM)
            } else if (timeDifference <= 60) {
              score += 0.5; // High bonus for imminent tasks
            } else if (timeDifference <= 120) {
              score += 0.2; // Medium bonus for upcoming tasks
            }
          }
        }
        
        if (score > bestScore) {
          bestScore = score;
          bestSchedule = { schedule, list, nextItem };
        }
      }
      
      if (!bestSchedule) {
        return null;
      }
      
      const { schedule, list, nextItem } = bestSchedule;
      
      // Generate reasoning
      const reasoning: string[] = [];
      const listData = universalCompletionService.getListDataForContext(list, new Date());
      const progress = listData ? listData.progress : 0;
      if (list.status === 'in_progress') reasoning.push('List is in progress');
      if (progress > 0) reasoning.push('Previous items completed');
      if (list.priority <= 2) reasoning.push('High priority list');
      if (list.deadline) {
        const daysUntilDeadline = Math.ceil((list.deadline.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
        if (daysUntilDeadline <= 7) reasoning.push('Deadline approaching');
      }
      
      // Add schedule timing reasoning
      if (schedule.recurringPattern?.time) {
        const [scheduleHour, scheduleMinute] = schedule.recurringPattern.time.split(':').map(Number);
        const scheduleTime = scheduleHour * 60 + scheduleMinute;
        const timeDifference = currentTime - scheduleTime;
        
        if (schedule.type === 'daily') {
          // For daily tasks, especially morning routines at 00:00
          if (scheduleHour === 0) {
            if (currentHour >= 6 && currentHour <= 12) {
              reasoning.push('Morning routine time');
            } else if (currentHour >= 12 && currentHour <= 18) {
              reasoning.push('Daily routine available');
            } else {
              reasoning.push('Daily routine pending');
            }
          } else if (timeDifference >= -60 && timeDifference <= 0) {
            reasoning.push('Scheduled time approaching');
          } else if (timeDifference > 0 && timeDifference <= 120) {
            reasoning.push('Daily task available');
          } else {
            reasoning.push('Daily routine task');
          }
        } else {
          // For weekly/monthly tasks, use precise timing
          if (timeDifference >= -60 && timeDifference <= 0) {
            reasoning.push('Scheduled time approaching');
          } else if (timeDifference > 0 && timeDifference <= 30) {
            reasoning.push('Currently scheduled');
          } else if (timeDifference > 30) {
            reasoning.push('Overdue from schedule');
          } else {
            reasoning.push('Upcoming scheduled task');
          }
        }
      }
      
      if (schedule.recurringPattern?.daysOfWeek) {
        reasoning.push(`Scheduled for ${currentDay}`);
      }
      
      reasoning.push('Next logical task in sequence');
      
      // Calculate confidence based on various factors
      let confidence = 0.7; // Base confidence
      if (list.status === 'in_progress') confidence += 0.1;
      if (progress > 0) confidence += 0.1;
      if (list.priority <= 2) confidence += 0.1;
      
      return {
        notification: {
          title: `Next: ${nextItem.content}`,
          message: `Continue with "${list.title}" - Complete your next task to stay on track!`,
          priority: list.priority <= 2 ? 'high' : 'medium',
          confidence: Math.min(confidence, 1.0),
          contextualFactors: reasoning,
          actions: this.getDefaultActions()
        },
        scheduleInfo: {
          listId: list.id,
          listTitle: list.title,
          nextItem: {
            content: nextItem.content,
            description: nextItem.description,
            priority: nextItem.priority
          },
          confidence: Math.min(confidence, 1.0),
          reasoning
        }
      };
      
    } catch (error) {
      console.error('Failed to get next scheduled notification:', error);
      return null;
    }
  }

  // === PUBLIC ANALYTICS ===

  /**
   * Gets notification analytics
   */
  getNotificationAnalytics(): {
    totalSent: number;
    totalRead: number;
    averageResponseTime: number;
    topActions: { action: string; count: number }[];
  } {
    const totalSent = this.activeNotifications.length;
    const totalRead = this.activeNotifications.filter(n => n.isRead).length;
    
    return {
      totalSent,
      totalRead,
      averageResponseTime: 0, // Would need to track response times
      topActions: [
        { action: 'complete', count: 0 },
        { action: 'snooze', count: 0 },
        { action: 'dismiss', count: 0 }
      ]
    };
  }

  /**
   * Sets up service worker message listener for notification actions
   */
  private setupServiceWorkerMessageListener(): void {
    if ('serviceWorker' in navigator) {
      // console.log('🔧 Setting up service worker message listener');
      
      // Add a global listener to catch all service worker messages for debugging
      const messageHandler = (event: MessageEvent) => {
        // console.log('📨 Service Worker message received (global handler):', event.data, 'from:', event.origin);
        
        if (event.data && event.data.type === 'NOTIFICATION_ACTION') {
          const { action, notificationId, data } = event.data;
          console.log('🎯 Service Worker notification action:', { action, notificationId, data });
          this.handleNotificationAction(notificationId, action, data || {});
        } else if (event.data && event.data.type === 'NOTIFICATION_CLICKED') {
          const { notificationId, scheduleId } = event.data;
          console.log('👆 Service Worker notification clicked:', { notificationId, scheduleId });
          this.markAsRead(notificationId);
          this.notifyListeners('notification_clicked', { notificationId, scheduleId });
        } else if (event.data && event.data.type === 'NOTIFICATION_CLOSED') {
          const { notificationId } = event.data;
          console.log('❌ Service Worker notification closed:', { notificationId });
        }
      };
      
      // Listen on navigator.serviceWorker (the standard way)
      navigator.serviceWorker.addEventListener('message', messageHandler);
      
      // Also listen on window (alternative way that sometimes works better)
      window.addEventListener('message', messageHandler);
      
      // Also listen on the service worker ready promise to ensure we catch all messages
      navigator.serviceWorker.ready.then(() => {
        console.log('✅ Service Worker is ready, message listener active');
        
        // Add a test method to the global window object for debugging
        (window as any).testServiceWorkerMessage = () => {
          console.log('🧪 Testing service worker message communication...');
          navigator.serviceWorker.ready.then((registration) => {
            if (registration.active) {
              // Test by sending a message to ourselves (this will help debug message flow)
              navigator.serviceWorker.controller?.postMessage({
                type: 'TEST_MESSAGE',
                data: 'Hello from main thread'
              });
            }
          });
        };
        
        console.log('🛠️ Debug function added: window.testServiceWorkerMessage()');
      });
    } else {
      console.warn('⚠️ Service Worker not supported in this browser');
    }
  }
}

// Singleton instance
export const smartNotificationService = new SmartNotificationService();

// Add to window for testing in development
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  (window as any).smartNotificationService = smartNotificationService;
  (window as any).debugNotifications = () => smartNotificationService.debugNotificationCompletion();
  (window as any).cleanupDuplicates = () => smartNotificationService.debugAndCleanDuplicates();
  
  // Add real-time notification monitoring
  (window as any).monitorNotifications = () => {
    console.log('🔍 Starting real-time notification monitoring...');
    
    const originalSendScheduleNotification = smartNotificationService.sendScheduleNotification.bind(smartNotificationService);
    
    // @ts-ignore
    smartNotificationService.sendScheduleNotification = async function(...args) {
      const [scheduleId, listId, title, , type] = args;
      console.log('📤 Notification being sent:', {
        scheduleId,
        listId,
        title: title?.substring(0, 50),
        type,
        timestamp: new Date().toISOString()
      });
      
      // Check for potential duplicates before sending
      const activeNotifications = this.getActiveNotifications();
      const activeCount = activeNotifications.length;
      const similarCount = activeNotifications.filter((n: any) => 
        n.scheduleId === scheduleId || 
        (n.title === title && n.scheduleId?.includes(listId))
      ).length;
      
      if (similarCount > 0) {
        console.warn('⚠️ Potential duplicate detected:', {
          scheduleId,
          title: title?.substring(0, 30),
          similarExistingNotifications: similarCount,
          totalActiveNotifications: activeCount
        });
      }
      
      const result = await originalSendScheduleNotification(...args);
      
      console.log('✅ Notification sent, total active:', this.getActiveNotifications().length);
      return result;
    };
    
    console.log('✅ Notification monitoring active. All outgoing notifications will be logged.');
    console.log('📋 Current active notifications:', smartNotificationService.getActiveNotifications().length);
  };
  
  (window as any).stopMonitoring = () => {
    // This would reset monitoring, but for simplicity we'll just log
    console.log('🔄 To stop monitoring, refresh the page');
  };
  
  // console.log('🛠️ SmartNotificationService available at window.smartNotificationService');
  // console.log('🧪 Debug methods available:');
  // console.log('  - debugNotifications() - Debug all active notifications');
  // console.log('  - cleanupDuplicates() - Find and remove duplicate notifications');
  // console.log('  - monitorNotifications() - Start real-time notification monitoring');
  // console.log('  - stopMonitoring() - Instructions to stop monitoring');
  // console.log('  - smartNotificationService.debugNotificationCompletion(scheduleId) - Debug specific notification');
  // console.log('  - smartNotificationService.validateScheduleDataConsistency() - Check data consistency');
  // console.log('  - smartNotificationService.debugScheduleResolution(scheduleId) - Test schedule resolution');
}